import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { chartColors } from '../../helpers/chartColors';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOPressureAnalysisHistogramChart = ({currentRTFO, monitorWellEvents, fiberData, monitorUWI, theme, darkMode, gridView}) => {

  const maxStageNumber = Math.max(...(Object.keys(currentRTFO?.perfSchedule?.wells)?.map(wellName => Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0]))?.flat(Infinity));
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (currentRTFO && (monitorWellEvents?.length > 0 || fiberData?.length > 0)) {
      return getChartData();
    };

    function getChartData() {
      try {
        
        // add traces
        let traces = [];

        let filteredEvents = [];

        if (monitorWellEvents?.length > 0) {
          filteredEvents = filteredEvents.concat(monitorWellEvents?.filter(e => e?.name !== 'Stage Start')); // show all events from all monitor wells 
        };
        if (fiberData?.length > 0) {
          filteredEvents = filteredEvents.concat(fiberData);
          // filteredEvents = filteredEvents.concat(fiberData?.filter(e => e?.monitorName === monitorUWI));
        };
        // const wellsWithEvents = [...new Set(filteredEvents?.map(e => e?.stageName?.split('_')?.[1]))];
        const allWells = Object.keys(currentRTFO?.surveys?.wells);


        for (let i = 0; i < allWells?.length; i++) {
          const wellName = allWells?.[i];
          const wellEvents = filteredEvents?.filter(e => e?.stageName?.split('_')?.[1]?.toLowerCase() === wellName);
          const color = chartColors[i % chartColors?.length];

          // const maxStageNumber = Math.max(...wellEvents.map(e => +(e.stageNumber || e.stageName?.split('_')?.[2])));

          // let wellGroupAdded = false;

          const X = [];
          const YVFR = [];
          const YDiffusivity = [];
          const YErrorVFR = [];
          const YErrorDiffusivity = [];

          for (let stageNumber = 1; stageNumber <= maxStageNumber; stageNumber++) {
            const events = wellEvents.filter(e => +(e.stageNumber || e.stageName?.split('_')?.[2]) === stageNumber);

            // if (!events?.length > 0) continue;

            const YStageVFR = [];
            const YStageDiffusivity = [];

            for (let i = 0; i < events?.length; i++) {
              const event = events[i];

              const vfr = (event?.slurryVolume || event?.vfr || 0) * (units === 'field' ? rtfoConversionFactors.slurryVolume : 1);
              const diffusivity = (event?.diffusivity || 0) * (units === 'field' ? rtfoConversionFactors.diffusivity : 1);

              if (vfr > 0) {
                YStageVFR.push(vfr);
              };
              if (diffusivity > 0) {
                YStageDiffusivity.push(diffusivity);
              };
            };

            YStageVFR.sort((a, b) => a - b);
            YStageDiffusivity.sort((a, b) => a - b);

            YVFR.push((YStageVFR?.reduce((tot, curr) => tot + curr, 0) / YStageVFR?.length) || 0);
            YDiffusivity.push((YStageDiffusivity?.reduce((tot, curr) => tot + curr, 0) / YStageDiffusivity?.length) || 0);
            X.push(stageNumber);
            YErrorVFR.push(((YStageVFR?.[YStageVFR?.length - 1] - YStageVFR?.[0]) / 2) || 0);
            YErrorDiffusivity.push(((YStageDiffusivity?.[YStageDiffusivity?.length - 1] - YStageDiffusivity?.[0]) / 2) || 0);
          };

          traces.push({ // vfr trace
            x: X,
            y: YVFR,
            type: 'bar',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `${wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1, ))} VFR (${units === 'field' ? 'bbl' : 'm3'})`,
            marker: {
              color
            },
            error_y: {
              type: 'data',
              array: YErrorVFR,
              visible: true,
              color: darkMode ? 'grey' : 'black'
            },
            legendgroup: wellName,
            showlegend: true
            // showlegend: !wellGroupAdded ? true : false
          });
        };

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentRTFO, darkMode, fiberData, maxStageNumber, monitorWellEvents, units]);


  const layout = {
    barmode: 'group',
    yaxis: { // VFR y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `VFR (${units === 'field' ? 'bbl' : 'm3'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      side: 'left',
			rangemode: 'nonnegative'
    },
    yaxis2: { // diffusivity y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Diffusivity',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      side: 'right',
      overlaying: 'y',
			rangemode: 'nonnegative',
      tick0: 1,
      dtick: 1
    },
    xaxis: { // Stage number axis
      title: {
        text: 'Stage Number',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: false,
			rangemode: 'nonnegative',
      // type: 'category'
      // spikethickness: 2,
      // spikedash: "dot",
      // spikecolor: "#999999",
      // spikemode: "across"
    },
    hovermode: "closest",
    // hoverdistance: 10, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.15
    },
    annotations: [
      {
        text: "VFR",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    shapes: [
      
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.07 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}_xf_${new Date().toISOString().split('T')[0]}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};
